import UUID from 'uuid';
import URI from 'urijs';
import { ISunnyRequestBuilderParams, SunnyRequestBuilder } from '@/models/Common/SunnyRequestBuilder';
import { IRequestWrap } from '@/server/IServer';
import { checkIsServer } from '@/utils/functions/checkServerEnvironment';

interface IPromotionPlatformRequestBuilderParams extends ISunnyRequestBuilderParams {
  apiKey?: string | undefined;
  caller?: string | undefined;
  memberNo?: string | undefined;
}

interface IPromotionPlatformApiRequestBuilderConstructor<T extends SunnyRequestBuilder> {
  new (params?: IPromotionPlatformRequestBuilderParams): T;
  /** @static */
  fromReq(req: IRequestWrap): T;
  /** @static */
  getBaseURIFromReq(req: IRequestWrap): URI;
}

export abstract class PromotionPlatformApiRequestBuilder extends SunnyRequestBuilder {
  private apiKey: string | undefined;
  private caller: string | undefined;
  private memberNo: string | undefined;

  constructor(params: IPromotionPlatformRequestBuilderParams = {}) {
    super(params);

    const { apiKey, caller, memberNo } = params;
    this.apiKey = apiKey;
    this.caller = caller;
    this.memberNo = memberNo;
  }

  static fromReq<T extends SunnyRequestBuilder>(
    this: IPromotionPlatformApiRequestBuilderConstructor<T>,
    req: IRequestWrap,
  ): T {
    if (!req || !req.config) {
      return new this();
    }

    const device = req.ua.getDevice();
    return new this({
      baseURI: this.getBaseURIFromReq(req),
      cgntId: req.cookies['cgntId'] || req.sunnyMeta['cgntId'],
      clientIP: (req.headers['x-forwarded-for'] as string) || req.connection.remoteAddress,
      platform: device.type === 'mobile' || device.type === 'tablet' ? 'MWeb' : 'Web',
      userAgent: req.ua.getUA(),
      ywt: req.session.getYWT(),
      testProduct: req.session.getSettings?.().testProduct,
      traceID: (req.headers['X-Trace-Id'] as string) || UUID.v4(),
      apiKey: req.config.PromotionPlatformConfig.apiKey,
      caller: req.config.PromotionPlatformConfig.caller,
      memberNo: req.session.memberNo,
    });
  }

  static getBaseURIFromReq<T extends SunnyRequestBuilder>(
    this: IPromotionPlatformApiRequestBuilderConstructor<T>,
    req: IRequestWrap,
  ): URI {
    return req.config.PromotionPlatform;
  }

  protected getAPIEndpoint(path: string): string {
    const baseURI = this.baseURI.href();
    return baseURI ? `${baseURI}${path}` : `${baseURI}/api/v1${path}`;
  }

  /**
   * @see {@link https://yanolja.atlassian.net/wiki/spaces/PF/pages/70849306}
   */
  protected makeHeaders(headers?: Record<string, string | undefined>): Record<string, string | undefined> | undefined {
    const defaultHeader = super.makeHeaders(headers);
    const proxyHeader = checkIsServer()
      ? {
          accept: 'application/json',
          'api-key': this.apiKey,
          caller: this.caller,
          'member-no': this.memberNo,
        }
      : {};

    return {
      ...defaultHeader,
      ...proxyHeader,
    };
  }
}
